import { uniq } from 'lodash';
import type { ProviderServiceDelegate } from '../cloudProvider/providerService.delegate';

export interface IInstanceType {
  account: string;
  key?: string;
  name: string;
  region: string;
}

export interface IInstanceTypesByRegion {
  [region: string]: IInstanceType[];
}

export interface IInstanceStorage {
  type: string;
  size?: number;
  count?: number;
  isDefault?: boolean;
}

export interface IPreferredInstanceType {
  name: string;
  nameRegex?: RegExp;
  label?: string;
  cpu?: number;
  memory?: number;
  storage?: IInstanceStorage;
  costFactor?: number;
  unavailable?: boolean;
  helpFieldKey?: string;
}

export interface IInstanceTypeFamily {
  type: string;
  description?: string;
  storageHelpFieldKey?: string;
  storageType?: string;
  instanceTypes: IPreferredInstanceType[];
}

export interface IInstanceTypeCategory {
  type: string;
  label?: string;
  families: IInstanceTypeFamily[];
  icon?: string;
  description?: string;
  stats?: {
    families: string[];
    cpu: { min: number; max: number };
    memory: { min: number; max: number };
    storage: { min: number; max: number };
    costFactor?: number;
  };
}

export interface IInstanceTypeService {
  resolveInstanceTypeDetails?: (instanceType: string) => PromiseLike<IPreferredInstanceType>;
  getCategories(): PromiseLike<IInstanceTypeCategory[]>;
  getAllTypesByRegion(): PromiseLike<IInstanceTypesByRegion>;
  getAvailableTypesForRegions(instanceTypes: string[], regions: string[]): PromiseLike<string[]>;
  getCategoryForInstanceType(instanceType: string): string;
}

export class InstanceTypeService {
  public constructor(private providerServiceDelegate: ProviderServiceDelegate) {}

  public getCategories(cloudProvider: string): Promise<IInstanceTypeCategory[]> {
    return Promise.resolve(this.getDelegate(cloudProvider).getCategories());
  }

  public getAllTypesByRegion(cloudProvider: string): Promise<IInstanceTypesByRegion> {
    return Promise.resolve(this.getDelegate(cloudProvider).getAllTypesByRegion());
  }

  public getAvailableTypesForRegions(
    cloudProvider: string,
    instanceTypes: string[],
    regions: string[],
  ): Promise<string[]> {
    return Promise.resolve(this.getDelegate(cloudProvider).getAvailableTypesForRegions(instanceTypes, regions));
  }

  public getCategoryForInstanceType(cloudProvider: string, instanceType: string) {
    return this.getInstanceTypeCategory(cloudProvider, instanceType).then((category: IInstanceTypeCategory) => {
      return category ? category.type : 'custom';
    });
  }

  public getCategoryForMultipleInstanceTypes(cloudProvider: string, instanceTypes: string[]) {
    const promises = instanceTypes.map((it) => this.getCategoryForInstanceType(cloudProvider, it));

    return Promise.all(promises).then((categories: string[]) => {
      const distinctCategories = uniq(categories);
      return distinctCategories.length === 1 ? distinctCategories[0] : 'custom';
    });
  }

  public getInstanceTypeDetails(cloudProvider: string, instanceType: string): Promise<IPreferredInstanceType> {
    return this.getInstanceTypeCategory(cloudProvider, instanceType).then((category: IInstanceTypeCategory) => {
      if (category && category.families && category.families.length && category.families[0].instanceTypes) {
        return category.families[0].instanceTypes.find((i) => i.name === instanceType);
      } else {
        if (this.getDelegate(cloudProvider).resolveInstanceTypeDetails) {
          return this.getDelegate(cloudProvider).resolveInstanceTypeDetails(instanceType);
        }
        return null;
      }
    });
  }

  private getInstanceTypeCategory(cloudProvider: string, instanceType: string): Promise<IInstanceTypeCategory> {
    return this.getCategories(cloudProvider).then((categories: IInstanceTypeCategory[]) => {
      return (categories || []).find((c) =>
        c.families.some((f) =>
          f.instanceTypes.some((i) => i.name === instanceType || (i.nameRegex && i.nameRegex.test(instanceType))),
        ),
      );
    });
  }

  private getDelegate(cloudProvider: string): IInstanceTypeService {
    return this.providerServiceDelegate.getDelegate<IInstanceTypeService>(
      cloudProvider,
      'instance.instanceTypeService',
    );
  }
}
